import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Input, Message, Select, Table, Tag, Typography } from "@arco-design/web-react";
import { IconDelete } from "@arco-design/web-react/icon";

import { FetchApi, PostApi, DeleteApi } from "../../Api/AuthenticatedApi";

const { Text } = Typography;

const EVENT_OPTIONS = [
  { label: "Contact subscribed", value: "contact.subscribed" },
  { label: "Contact unsubscribed", value: "contact.unsubscribed" },
  { label: "Contact bounced", value: "contact.bounced" },
  { label: "Contact complained", value: "contact.complained" },
];

export default function WebhookSettings() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [endpoints, setEndpoints] = useState([]);
  const [refresh, setRefresh] = useState(true);

  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!refresh) return;
    FetchApi(getAccessTokenSilently, "/api/webhooks/",
      (data) => { setEndpoints(data || []); setRefresh(false); },
      () => navigate("/login"),
      () => { setRefresh(false); Message.error("Failed to load webhooks"); });
  }, [getAccessTokenSilently, navigate, refresh]);

  const handleAdd = () => {
    if (!url.trim()) { Message.warning("Enter a URL"); return; }
    PostApi(getAccessTokenSilently, "/api/webhooks/",
      () => { setUrl(""); setSecret(""); setEvents([]); Message.success("Webhook added"); setRefresh(true); },
      () => navigate("/login"),
      () => Message.error("Failed to add webhook"),
      { url: url.trim(), secret: secret.trim(), enabled: true, event_types: events.join(",") });
  };

  const handleTest = (id) => {
    PostApi(getAccessTokenSilently, "/api/webhooks/" + id + "/test",
      () => Message.success("Test event delivered — your endpoint responded OK"),
      () => navigate("/login"),
      (err) => Message.error((err && err.message) || "Test delivery failed"),
      {});
  };

  const handleDelete = (id) => {
    DeleteApi(getAccessTokenSilently, "/api/webhooks/" + id,
      () => { Message.success("Webhook removed"); setRefresh(true); },
      () => navigate("/login"),
      () => Message.error("Failed to remove webhook"),
      {});
  };

  const columns = [
    { title: "URL", dataIndex: "url", render: (u) => <Text code>{u}</Text> },
    {
      title: "Events",
      dataIndex: "event_types",
      render: (et) => (et ? et.split(",").map((e) => <Tag key={e} size="small" style={{ marginRight: 4 }}>{e.trim()}</Tag>) : <Tag size="small" color="arcoblue">all</Tag>),
    },
    {
      title: "",
      render: (_, e) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button size="small" type="outline" onClick={() => handleTest(e.id)}>Test</Button>
          <Button size="small" shape="circle" type="text" icon={<IconDelete />} onClick={() => handleDelete(e.id)} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Text type="secondary">
        Send a POST to your URL when contact events happen. Each request carries an{" "}
        <Text code>X-JCMailer-Event</Text> header and an <Text code>X-JCMailer-Signature</Text>{" "}
        header (<Text code>sha256=</Text> + HMAC-SHA256 of the raw body, keyed by your secret) —
        verify it to confirm the request came from us. The JSON body looks like:
      </Text>
      <pre style={{
        background: "var(--color-fill-2)", borderRadius: 4, padding: 12, marginTop: 8,
        fontSize: 12, overflowX: "auto",
      }}>{`{
  "id": "9f1c…",                  // unique per event — use it to dedupe
  "event": "contact.subscribed",
  "created_at": "2026-06-29T20:00:00Z",
  "data": { "email": "ada@example.com", "contact_id": "…" }
}`}</pre>

      <Table
        style={{ marginTop: 16 }}
        size="small"
        columns={columns}
        data={endpoints}
        rowKey="id"
        pagination={false}
        border={false}
        noDataElement={<span style={{ color: "var(--color-text-3)" }}>No webhooks yet.</span>}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <Input style={{ width: 280 }} placeholder="https://your-app.com/hooks" value={url} onChange={setUrl} />
        <Input style={{ width: 160 }} placeholder="Signing secret (optional)" value={secret} onChange={setSecret} />
        <Select
          mode="multiple"
          style={{ minWidth: 220 }}
          placeholder="All events"
          value={events}
          onChange={setEvents}
          options={EVENT_OPTIONS}
        />
        <Button type="primary" onClick={handleAdd}>Add webhook</Button>
      </div>
    </div>
  );
}
